import {
    NotificationSubscriptionCategory,
    convertSubscriptionsToValues,
    convertModulesToValues,
} from "modules/common";
import {
    MappedUserProfile,
    NotificationSettings,
    NotificationSubscriptions,
    updateModuleManagement,
    updateNotificationSubscription,
} from "modules/dashboard";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { ModuleManagement } from "../ModuleManagement/ModuleManagement";

interface Props {
    modules: Array<{ values: number[] }>;
    roleType?: number;
    profile: MappedUserProfile | null;
}

export const Settings: React.FC<Props> = ({ modules, roleType, profile }) => {
    const dispatch = useDispatch();

    const subscriptions = useMemo(
        () => [
            {
                category: NotificationSubscriptionCategory.InfoTypeSubscription,
                values: profile?.infoType ?? [],
            },
            {
                category: NotificationSubscriptionCategory.NotificationTypeSubscription,
                values: profile?.notificationType ?? [],
            },
            {
                category: NotificationSubscriptionCategory.PlantProtectionCountySubscription,
                values: profile?.plantProtectionCounties ?? [],
            },
            {
                category: NotificationSubscriptionCategory.NewsProfessionalFieldSubscription,
                values: profile?.infoProfessionalField ?? [],
            },
            {
                category:
                    NotificationSubscriptionCategory.MainfestationsProfessionalFieldSubscription,
                values: profile?.manifestationsProfessionalField ?? [],
            },
            {
                category: NotificationSubscriptionCategory.ManifestationsCountySubscription,
                values: profile?.manifestationsCounties ?? [],
            },
            {
                category: NotificationSubscriptionCategory.RecordsDeadlineSubscription,
                values: profile?.recordsDeadline ?? [],
            },
            {
                category: NotificationSubscriptionCategory.EducationsCountiesSubscription,
                values: profile?.educationCounties ?? [],
            },
            {
                category: NotificationSubscriptionCategory.EducationsProfessionalFieldSubscription,
                values: profile?.educationProfessionalField ?? [],
            },
            {
                category: NotificationSubscriptionCategory.EducationsInterventionsSubscription,
                values: profile?.educationInterventions ?? [],
            },
        ],
        [profile]
    );

    const [activatedSubscriptions, setActivatedSubscriptions] = useState<string[]>([]);
    const [activatedModules, setActivatedModules] = useState<string[]>([]);

    const handleNotificationsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        const splitValues = value.split("_");
        const category = splitValues[0] as NotificationSubscriptionCategory;
        const id = parseInt(splitValues[1]);
        dispatch(updateNotificationSubscription(category, id));

        const subscriptionKey = `${category}_${id}`;
        setActivatedSubscriptions(prev =>
            prev.includes(subscriptionKey)
                ? prev.filter(subscription => subscription !== subscriptionKey)
                : [...prev, subscriptionKey]
        );
    };

    const handleModulesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        const id = parseInt(value);
        dispatch(updateModuleManagement(id));
    };

    useEffect(() => {
        const newSubscriptions = subscriptions.flatMap(({ category, values }) =>
            convertSubscriptionsToValues(values, category)
        );
        setActivatedSubscriptions(newSubscriptions);
    }, [subscriptions]);

    useEffect(() => {
        if (modules?.length) {
            const newModules = modules
                .flatMap(({ values }) => convertModulesToValues(values))
                .map(String);

            setActivatedModules(newModules);
        } else {
            setActivatedModules([]);
        }
    }, [modules]);

    return (
        <>
            <section>
                <NotificationSettings
                    onChange={handleNotificationsChange}
                    activatedSubscriptions={activatedSubscriptions}
                />
            </section>
            <section>
                <NotificationSubscriptions
                    onChange={handleNotificationsChange}
                    activatedSubscriptions={activatedSubscriptions}
                    roleType={roleType}
                />
            </section>
            <section>
                <ModuleManagement
                    onChange={handleModulesChange}
                    activatedModules={activatedModules}
                    roleType={roleType}
                />
            </section>
        </>
    );
};
